import type { Object3D } from 'three';
import { Color, Mesh } from 'three';

/**
 * Material names that must NOT be tinted when applying the placeholder
 * appearance. These are special-cased by the standard model material pipeline
 * (`adjustSingleMaterial` in model-helpers.ts) — e.g. transparent arch material
 * above WOOD/LOG-INSERT — and re-tinting them would either be invisible or
 * break their intentional appearance.
 */
const SKIP_MATERIAL_NAMES = new Set<string>([
	'Image-arch'
]);

/**
 * Apply a salmon-red emissive tint to every mesh on the given model so it
 * reads as a "placeholder" representation of a different appliance.
 *
 * Mirrors the salmon-red [1.0, 0.7, 0.7] visual cue used by the "no countertop"
 * option (locked decision: emissive 0xff4040, emissiveIntensity 0.4).
 *
 * Each material is cloned before mutation — the model-manager caches materials
 * across instances, and mutating the cached shared material would tint every
 * other instance of the same source model.
 */
export const applyPlaceholderTint = (model: Object3D): void => {
	model.traverse((child: Object3D) => {
		if (!(child instanceof Mesh) || !child.material) return;

		if (Array.isArray(child.material)) {
			child.material = child.material.map((mat) => tintMaterial(mat));
		} else {
			child.material = tintMaterial(child.material);
		}
	});
};

const tintMaterial = (material: any): any => {
	if (!material) return material;
	if (SKIP_MATERIAL_NAMES.has(material.name)) return material;
	// Only PBR-style materials support emissive — skip basic/line materials.
	if (!('emissive' in material) || !('emissiveIntensity' in material)) return material;

	const cloned = material.clone();
	cloned.emissive = new Color(0xff4040);
	cloned.emissiveIntensity = 0.4;
	cloned.needsUpdate = true;
	return cloned;
};
